import React, { useEffect, useMemo, useRef, useState } from 'react';
import './BtnWhatsapp.css';
import whatsappIcon from '../../images/wpp.png';
import logoIcon from '../../images/logo.png';
import Modal from 'react-modal';
import baseURL from '../url';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { safeGet, safeSet, supportsMediaRecorder } from '../utils/browserSafe';
import { faPaperPlane, faChevronDown, faFaceSmile, faImage, faMicrophone, faStop, faPlay, faPause } from '@fortawesome/free-solid-svg-icons';

const EMOJIS = ['😀','😁','😂','🤣','😊','😍','😘','😎','😉','🙌','👏','🙏','❤️','🔥','👍','🎉','🛒','📦','✅','🥩'];

function getSessionKey() {
    const keyName = 'ky13_chat_session_key';
    let existing = safeGet(keyName, '');
    if (!existing) {
        existing = 'sess_' + Math.random().toString(36).slice(2) + Date.now();
        safeSet(keyName, existing);
    }
    return existing;
}


function formatAudioTime(seconds) {
    const total = Number.isFinite(seconds) ? Math.max(0, Math.floor(seconds)) : 0;
    const mins = Math.floor(total / 60);
    const secs = total % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function AudioMessage({ src, duration = null, isUser = false }) {
    const audioRef = useRef(null);
    const [playing, setPlaying] = useState(false);
    const [current, setCurrent] = useState(0);
    const [total, setTotal] = useState(duration || 0);

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;
        const onLoaded = () => {
            if (audio.duration && Number.isFinite(audio.duration)) {
                setTotal(audio.duration);
            }
        };
        const onTime = () => setCurrent(audio.currentTime || 0);
        const onEnded = () => {
            setPlaying(false);
            setCurrent(0);
        };
        const onPause = () => setPlaying(false);
        const onPlay = () => setPlaying(true);
        audio.addEventListener('loadedmetadata', onLoaded);
        audio.addEventListener('timeupdate', onTime);
        audio.addEventListener('ended', onEnded);
        audio.addEventListener('pause', onPause);
        audio.addEventListener('play', onPlay);
        return () => {
            audio.removeEventListener('loadedmetadata', onLoaded);
            audio.removeEventListener('timeupdate', onTime);
            audio.removeEventListener('ended', onEnded);
            audio.removeEventListener('pause', onPause);
            audio.removeEventListener('play', onPlay);
        };
    }, [src]);

    const togglePlay = () => {
        const audio = audioRef.current;
        if (!audio) return;
        if (playing) audio.pause();
        else {
            try { audio.play(); } catch (e) { console.error('No se pudo reproducir audio', e); }
        }
    };

    const progress = total > 0 ? Math.min(100, Math.max(0, (current / total) * 100)) : 0;

    return (
        <div className={`audioMessageCard ${isUser ? 'audioMessageCardUser' : 'audioMessageCardSupport'}`}>
            <audio ref={audioRef} preload="metadata" src={src} className='nativeHiddenAudio' controls />
            <button type='button' className='audioPlayBtn' onClick={togglePlay} aria-label={playing ? 'Pausar audio' : 'Reproducir audio'}>
                <FontAwesomeIcon icon={playing ? faPause : faPlay} />
            </button>
            <div className='audioMain'>
                <div className='audioProgressTrack'>
                    <span className='audioProgressFill' style={{ width: `${progress}%` }} />
                </div>
                <div className='audioMeta'>
                    <span>{playing ? 'Reproduciendo audio' : 'Audio'}</span>
                    <span>{formatAudioTime(total || duration || 0)}</span>
                </div>
            </div>
        </div>
    );
}

export default function BtnWhatsapp() {
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [noteText, setNoteText] = useState('');
    const [session, setSession] = useState(null);
    const [customer, setCustomer] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showEmoji, setShowEmoji] = useState(false);
    const [recording, setRecording] = useState(false);
    const [audioBlob, setAudioBlob] = useState(null);
    const [audioPreview, setAudioPreview] = useState('');
    const [imageFile, setImageFile] = useState(null);
    const bodyRef = useRef(null);
    const fileInputRef = useRef(null);
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const streamRef = useRef(null);
    const sessionKey = useMemo(() => getSessionKey(), []);

    const hasPhone = Boolean(session?.customer_phone || customer?.telefono);
    const lastMessage = messages[messages.length - 1];
    const waitingPhone = !hasPhone && lastMessage?.origin === 'system';
    const placeholder = waitingPhone ? 'Si desea, déjenos su número de teléfono...' : 'Escribe aquí tu mensaje...';

    const loadCustomerProfile = async () => {
        try {
            const res = await fetch(`${baseURL}/cliente_me.php`, { credentials: 'include' });
            const data = await res.json();
            if (data?.ok && data.cliente) {
                setCustomer(data.cliente);
                safeSet('ky13_customer_profile', JSON.stringify(data.cliente));
                return;
            }
        } catch (e) {}
        const local = safeGet('ky13_customer_profile');
        if (local) {
            try { setCustomer(JSON.parse(local)); } catch (e) {}
        }
    };

    useEffect(() => {
        loadCustomerProfile();
        const handler = (event) => setCustomer(event.detail || null);
        window.addEventListener('ky13CustomerUpdated', handler);
        return () => window.removeEventListener('ky13CustomerUpdated', handler);
    }, []);

    const scrollToBottom = () => {
        setTimeout(() => {
            if (bodyRef.current) {
                bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
            }
        }, 50);
    };

    const fetchBoot = async () => {
        try {
            setLoading(true);
            const res = await fetch(`${baseURL}/telegram_chat_boot.php?session_key=${encodeURIComponent(sessionKey)}`);
            const text = await res.text();
            const data = JSON.parse(text || '{}');
            if (data?.ok) {
                setSession(data.session || null);
                setMessages(data.messages || []);
                scrollToBottom();
            }
        } catch (error) {
            console.error('Error al iniciar chat Telegram:', error);
        } finally {
            setLoading(false);
        }
    };

    const pollMessages = async () => {
        try {
            const res = await fetch(`${baseURL}/telegram_chat_poll.php?session_key=${encodeURIComponent(sessionKey)}`);
            const text = await res.text();
            const data = JSON.parse(text || '{}');
            if (data?.ok) {
                setSession(data.session || null);
                setMessages(data.messages || []);
                scrollToBottom();
            }
        } catch (error) {
            console.error('Error al consultar mensajes:', error);
        }
    };

    useEffect(() => {
        if (!modalIsOpen) return;
        fetchBoot();
        const interval = setInterval(() => {
            pollMessages();
        }, 4000);
        return () => clearInterval(interval);
    }, [modalIsOpen]);

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const openModal = () => setModalIsOpen(true);
    const closeModal = () => {
        setModalIsOpen(false);
        setShowEmoji(false);
    };

    const resetMedia = () => {
        if (audioPreview) URL.revokeObjectURL(audioPreview);
        setAudioBlob(null);
        setAudioPreview('');
        setImageFile(null);
    };

    const handleSend = async () => {
        const text = noteText.trim();
        if (!text && !audioBlob && !imageFile) return;

        try {
            let options;
            if (audioBlob || imageFile) {
                const fd = new FormData();
                fd.append('session_key', sessionKey);
                if (customer?.telefono) fd.append('phone_profile', customer.telefono);
                if (waitingPhone) fd.append('phone', text);
                else if (text) fd.append('message', text);
                if (imageFile) fd.append('image', imageFile);
                if (audioBlob) {
                    fd.append('audio', audioBlob, 'chat-audio.webm');
                }
                options = { method: 'POST', body: fd };
            } else {
                const payload = { session_key: sessionKey };
                if (customer?.telefono) payload.phone_profile = customer.telefono;
                if (waitingPhone) payload.phone = text;
                else payload.message = text;
                options = {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload),
                };
            }

            const res = await fetch(`${baseURL}/telegram_chat_send.php`, options);
            const raw = await res.text();
            const data = JSON.parse(raw || '{}');

            if (data?.ok) {
                setMessages(data.messages || []);
                if (data.phone_saved) {
                    setSession(prev => ({ ...(prev || {}), customer_phone: text.replace(/\D+/g, '') }));
                }
                setNoteText('');
                setShowEmoji(false);
                resetMedia();
                scrollToBottom();
            } else if (data?.error) {
                console.error(data.error);
            }
        } catch (error) {
            console.error('Error enviando mensaje:', error);
        }
    };

    const toggleRecording = async () => {
        if (recording) {
            mediaRecorderRef.current?.stop();
            setRecording(false);
            return;
        }
        try {
            if (!supportsMediaRecorder()) { alert('La grabación de audio no está disponible en este navegador.'); return; }
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;
            const recorder = new MediaRecorder(stream);
            audioChunksRef.current = [];
            recorder.ondataavailable = (e) => {
                if (e.data.size > 0) audioChunksRef.current.push(e.data);
            };
            recorder.onstop = () => {
                const blob = new Blob(audioChunksRef.current, { type: recorder.mimeType || 'audio/webm' });
                setAudioBlob(blob);
                setAudioPreview(URL.createObjectURL(blob));
                stream.getTracks().forEach(t => t.stop());
            };
            mediaRecorderRef.current = recorder;
            recorder.start();
            setRecording(true);
        } catch (e) {
            console.error('No se pudo grabar audio', e);
        }
    };

    const onPickImage = (e) => {
        const file = e.target.files?.[0];
        if (file) setImageFile(file);
    };

    const addEmoji = (emoji) => setNoteText((prev) => prev + emoji);

    const renderMessage = (msg) => {
        const isUser = msg.origin === 'user';
        const rowClass = isUser ? 'user' : 'support';
        const bubbleClass = isUser ? 'userBubble' : 'supportBubble';
        const avatar = isUser ? null : <img src={logoIcon} alt='Mercadito KY-13' className='chatAvatar' />;
        const isImage = msg.message_type === 'image' && msg.attachment_url;
        const isAudio = msg.message_type === 'audio' && msg.attachment_url;

        let content = null;
        if (isImage) {
            content = (
                <>
                    {msg.message_text ? <div className='messageText'>{msg.message_text}</div> : null}
                    <img src={msg.attachment_url} alt='Adjunto' className='chatImage' />
                </>
            );
        } else if (isAudio) {
            content = (
                <>
                    {msg.message_text ? <div className='messageText audioText'>{msg.message_text}</div> : null}
                    <AudioMessage src={msg.attachment_url} duration={msg.attachment_duration ? Number(msg.attachment_duration) : null} isUser={isUser} />
                </>
            );
        } else {
            content = <div className='messageText'>{msg.message_text}</div>;
        }

        return (
            <div key={msg.id} className={`chatRow ${rowClass}`}>
                {!isUser && avatar}
                <div className={`chatBubble ${bubbleClass} ${isAudio ? 'audioBubble' : ''} ${isImage ? 'imageBubble' : ''}`}>{content}</div>
            </div>
        );
    };

    return (
        <div className='containWpp'>
            <button className='btnWhatsapp' onClick={openModal} aria-label='Abrir chat de soporte'>
                <img src={whatsappIcon} alt="whatsappIcon" />
            </button>

            <Modal
                isOpen={modalIsOpen}
                onRequestClose={closeModal}
                className="modal-wpp"
                overlayClassName="overlay-wpp"
            >
                <div className='containModalWpp'>
                    <div className='headerWpp'>
                        <div className='headerWppTop'>
                            <button className='conversationBadge' type='button'>Conversación</button>
                            <button onClick={closeModal} className='closeBtn' aria-label='Cerrar chat'>
                                <FontAwesomeIcon icon={faChevronDown} />
                            </button>
                        </div>
                        <div className='supportHead'>
                            <img src={logoIcon} alt='Mercadito KY-13' className='supportAvatar' />
                            <div className='supportMeta'>
                                <strong>Asistencia en línea</strong>
                                <span>Mercadito KY-13</span>
                            </div>
                        </div>
                    </div>

                    <div className='chatBody' ref={bodyRef}>
                        {loading && <div className='chatDate'>Cargando conversación...</div>}
                        {!loading && messages.map(renderMessage)}
                    </div>

                    {showEmoji && (
                        <div className='emojiPanel'>
                            {EMOJIS.map((emoji) => (
                                <button key={emoji} type='button' className='emojiBtn' onClick={() => addEmoji(emoji)}>{emoji}</button>
                            ))}
                        </div>
                    )}

                    <div className='sendWpp'>
                        {(imageFile || audioPreview) && (
                            <div className='pendingMedia'>
                                {imageFile ? <span>Imagen: {imageFile.name}</span> : null}
                                {audioPreview ? <audio controls src={audioPreview} className='chatAudio pendingAudio' /> : null}
                                <button type='button' className='clearMediaBtn' onClick={resetMedia}>Quitar</button>
                            </div>
                        )}
                        <textarea
                            placeholder={placeholder}
                            value={noteText}
                            onChange={(e) => setNoteText(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    handleSend();
                                }
                            }}
                        />
                        <div className='composerTools'>
                            <div className='leftTools'>
                                <button type='button' className='toolBtn' onClick={() => setShowEmoji((v) => !v)} aria-label='Emojis'>
                                    <FontAwesomeIcon icon={faFaceSmile} />
                                </button>
                                <button type='button' className='toolBtn' onClick={() => fileInputRef.current?.click()} aria-label='Imagen'>
                                    <FontAwesomeIcon icon={faImage} />
                                </button>
                                <input ref={fileInputRef} type='file' accept='image/*' style={{ display: 'none' }} onChange={onPickImage} />
                                <button type='button' className={`toolBtn ${recording ? 'recording' : ''}`} onClick={toggleRecording} aria-label='Grabar audio'>
                                    <FontAwesomeIcon icon={recording ? faStop : faMicrophone} />
                                </button>
                            </div>
                            <button onClick={handleSend} className='sendBtn' aria-label='Enviar mensaje'>
                                <FontAwesomeIcon icon={faPaperPlane} />
                            </button>
                        </div>
                    </div>
                </div>
            </Modal>
        </div>
    );
}
